import {
  someList,
  toggleBulletedList,
  toggleNumberedList,
} from "@platejs/list-classic";
import {
  BulletedListPlugin,
  NumberedListPlugin,
} from "@platejs/list-classic/react";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ListIcon from "@redotech/redo-icons/list.svg";
import NumberedListIcon from "@redotech/redo-icons/numbered-list_filled.svg";
import type { SlateEditor } from "platejs";
import { useEditorRef, useEditorSelector } from "platejs/react";
import { memo, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "../../arbiter-components/command-menu/redo-command-menu";
import { RedoIcon } from "../../arbiter-components/icon/redo-icon";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../ui/dropdown-menu";
import { useToolbarOverflow } from "./toolbar-overflow";

const LIST_ITEMS = [
  { Icon: ListIcon, text: "Bulleted list", toggle: toggleBulletedList },
  { Icon: NumberedListIcon, text: "Numbered list", toggle: toggleNumberedList },
] as const satisfies readonly {
  Icon: typeof ListIcon;
  text: string;
  toggle: (editor: SlateEditor) => void;
}[];

export const ListToolbarButton = memo(function ListToolbarButton() {
  const editor = useEditorRef();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  const isBulleted = useEditorSelector(
    (editor) => someList(editor, BulletedListPlugin.key),
    [],
  );
  const isNumbered = useEditorSelector(
    (editor) => someList(editor, NumberedListPlugin.key),
    [],
  );
  const ActiveIcon = isNumbered ? NumberedListIcon : ListIcon;

  const items: RedoCommandMenuItem[] = useMemo(
    () =>
      LIST_ITEMS.map(({ Icon, text, toggle }) => ({
        Icon,
        text,
        onClick: () => {
          toggle(editor);
          editor.tf.focus();
        },
      })),
    [editor],
  );

  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <RedoIcon Icon={ListIcon} size={16} />
          Lists
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {LIST_ITEMS.map(({ Icon, text, toggle }) => (
            <DropdownMenuItem
              key={text}
              onSelect={() => {
                toggle(editor);
                editor.tf.focus();
              }}
            >
              <RedoIcon Icon={Icon} size={16} />
              {text}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={ActiveIcon}
        IconTrailing={ChevronDownIcon}
        onClick={() => setOpen((prev) => !prev)}
        pressed={isBulleted || isNumbered}
        ref={setAnchor}
        size="sm"
        text=""
      />
      <RedoCommandMenu
        anchor={anchor}
        items={items}
        open={open}
        placement="bottom-start"
        setOpen={setOpen}
      />
    </>
  );
});

// Keep individual exports for backwards compatibility
export const BulletedListToolbarButton = ListToolbarButton;
export const NumberedListToolbarButton = ListToolbarButton;
export const TodoListToolbarButton = ListToolbarButton;
